/**
 * Irmão: `src/app/components/filters/FilterOverflow.tsx`.
 *
 * Duplicação deliberada (openspec/changes/flashcards-revisao-redesign/design.md,
 * Decisão 2). Não reusar `components/filters/`: store, persistência e efeito de
 * aplicação são de outra tela. Uma correção visual aqui deve ser conferida
 * no irmão, e o inverso.
 */
import { ChevronDown, ChevronUp, List } from "lucide-react";
import {
  REVISAO_FILTER_FOCUS_RING,
  REVISAO_VISIBLE_OPTIONS,
  RevisaoOverflowMode,
  revisaoFilterCx,
} from "./tokens";

export interface RevisaoFilterOverflowProps {
  mode: RevisaoOverflowMode;
  total: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onViewAll: () => void;
  /** Names the collection for screen readers, e.g. "matérias". */
  noun: string;
}

const linkClass = revisaoFilterCx(
  "inline-flex items-center gap-1.5 rounded-chip px-2 py-1",
  "text-[13px] font-medium text-brand transition-colors hover:bg-surface-hover",
  REVISAO_FILTER_FOCUS_RING,
);

/**
 * The one escalation path for a large option set.
 *
 * Small overflow expands in place; a genuinely large set opens the full list
 * instead. Exactly one control is ever visible — no arrow paging, no "ver mais"
 * sitting next to a "ver todas". The caller picks the rung with
 * `revisaoOverflowMode`: none at ≤12, inline at 13–24, modal above 24.
 */
export default function RevisaoFilterOverflow({
  mode,
  total,
  expanded,
  onToggleExpand,
  onViewAll,
  noun,
}: RevisaoFilterOverflowProps) {
  if (mode === "none") return null;

  if (mode === "inline") {
    const hidden = total - REVISAO_VISIBLE_OPTIONS;
    return (
      <button
        type="button"
        onClick={onToggleExpand}
        className={linkClass}
        aria-expanded={expanded}
        aria-label={
          expanded ? `Mostrar menos ${noun}` : `Mostrar mais ${hidden} ${noun}`
        }
      >
        {expanded ? (
          <>
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
            Mostrar menos
          </>
        ) : (
          <>
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
            {`+ ${hidden} mais`}
          </>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onViewAll}
      className={linkClass}
      aria-label={`Ver todas as ${total} opções de ${noun}`}
    >
      <List className="h-4 w-4" aria-hidden="true" />
      {`Ver todas (${total})`}
    </button>
  );
}
